/**
 * Enhanced Clinical Notes Component
 * Premium UX, Production-Grade, Therapist/Psychiatrist Focused
 * Comprehensive clinical note management with templates, rich text, categorization
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  useTheme,
  Tabs,
  Tab,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
  Paper,
  Divider,
  Tooltip,
  Menu,
  Badge,
  InputAdornment,
  alpha,
  Autocomplete,
  Avatar,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  addClinicalNote,
  updateClinicalNote,
  deleteClinicalNote,
  fetchClinicalNotes,
} from '../../features/patients/patientSlice';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Note as NoteIcon,
  Assessment as AssessmentIcon,
  LocalHospital as HospitalIcon,
  Medication as MedicationIcon,
  Science as LabIcon,
  Chat as ChatIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  ContentCopy as TemplateIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  Label as LabelIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO } from 'date-fns';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const NoteCard = styled(Paper)(({ theme, urgent }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius,
  border: `2px solid ${urgent 
    ? alpha(theme.palette.error.main, 0.3)
    : alpha(theme.palette.primary.main, 0.1)}`,
  background: urgent
    ? alpha(theme.palette.error.main, 0.05)
    : alpha(theme.palette.background.paper, 0.8),
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateX(8px)',
    boxShadow: theme.shadows[4],
    borderColor: urgent 
      ? theme.palette.error.main
      : theme.palette.primary.main,
  },
}));

// Note Types Configuration
const NOTE_TYPES = [
  {
    value: 'mse',
    label: 'Mental Status Exam',
    icon: <AssessmentIcon />,
    color: 'primary',
    template: {
      appearance: '',
      behavior: '',
      speech: '',
      mood: '',
      affect: '',
      thoughtProcess: '',
      thoughtContent: '',
      cognition: '',
      insight: '',
      judgment: '',
      orientation: { time: false, place: false, person: false, situation: false },
      suicidalIdeation: false,
      homicidalIdeation: false,
      hallucinations: false,
      delusions: false,
    },
  },
  {
    value: 'progress_note',
    label: 'Progress Note (SOAP)',
    icon: <NoteIcon />,
    color: 'info',
    template: {
      subjective: '',
      objective: '',
      assessment: '',
      plan: '',
      treatmentProgress: '',
      nextSteps: '',
    },
  },
  {
    value: 'initial_assessment',
    label: 'Initial Assessment',
    icon: <AssessmentIcon />,
    color: 'warning',
    template: {
      presentingComplaint: '',
      historyOfPresentIllness: '',
      mentalStateExamination: '',
      diagnosis: '',
      treatmentPlan: '',
    },
  },
  {
    value: 'discharge_summary',
    label: 'Discharge Summary',
    icon: <HospitalIcon />,
    color: 'success',
    template: {
      admissionDate: '',
      dischargeDate: '',
      reasonForAdmission: '',
      treatmentProvided: '',
      dischargeDiagnosis: '',
      followUpInstructions: '',
      dischargePlan: '',
    },
  },
  {
    value: 'medication_review',
    label: 'Medication Review',
    icon: <MedicationIcon />,
    color: 'secondary',
    template: {
      currentMedications: '',
      changes: '',
      sideEffects: '',
      adherence: '',
      recommendations: '',
    },
  },
  {
    value: 'consultation',
    label: 'Consultation Note',
    icon: <ChatIcon />,
    color: 'default',
    template: {
      consultationType: '',
      discussionPoints: '',
      recommendations: '',
      followUpRequired: false,
    },
  },
];

const TAG_OPTIONS = [
  'Crisis',
  'High Risk',
  'Follow-up Required',
  'Medication Change',
  'Therapy Session',
  'Assessment',
  'Review',
  'Urgent',
];

const EnhancedClinicalNotes = ({ patientId, role = 'doctor' }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const patientsState = useSelector((state) => state.patients);
  const clinicalNotes = patientsState?.clinicalNotes || null;
  const loading = patientsState?.clinicalNotes?.loading || false;
  const error = patientsState?.clinicalNotes?.error || null;

  // Local state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [tags, setTags] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'progress_note',
    date: format(new Date(), 'yyyy-MM-dd'),
    clinician: '',
    tags: [],
    urgent: false,
    // MSE fields
    appearance: '',
    behavior: '',
    speech: '',
    mood: '',
    affect: '',
    thoughtProcess: '',
    thoughtContent: '',
    cognition: '',
    insight: '',
    judgment: '',
    orientation: { time: false, place: false, person: false, situation: false },
    suicidalIdeation: false,
    homicidalIdeation: false,
    hallucinations: false,
    delusions: false,
    // SOAP fields
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    treatmentProgress: '',
    nextSteps: '',
    followUpDate: '',
  });

  // Flatten and process clinical notes
  const allClinicalNotes = useMemo(() => {
    if (!clinicalNotes) return [];
    const notes = [
      ...(clinicalNotes.mse || []),
      ...(clinicalNotes.progressNotes || []),
      ...(clinicalNotes.dischargeSummaries || []),
      ...(clinicalNotes.initialAssessments || []),
      ...(clinicalNotes.medicationReviews || []),
      ...(clinicalNotes.consultationNotes || []),
    ];

    return notes.map(note => ({
      ...note,
      created_at: note.created_at || note.date || new Date().toISOString(),
      type: note.type || 'progress_note',
      tags: note.tags || [],
      urgent: note.urgent || false,
    }));
  }, [clinicalNotes]);

  // Filtered notes
  const filteredNotes = useMemo(() => {
    let filtered = [...allClinicalNotes];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(note =>
        note.title?.toLowerCase().includes(searchLower) ||
        note.content?.toLowerCase().includes(searchLower) ||
        note.diagnosis?.toLowerCase().includes(searchLower)
      );
    }

    if (filterType) {
      filtered = filtered.filter(note => note.type === filterType);
    }

    if (filterTag) {
      filtered = filtered.filter(note => 
        note.tags?.includes(filterTag) || note.urgent
      );
    }

    return filtered.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });
  }, [allClinicalNotes, searchTerm, filterType, filterTag]);

  // Fetch notes on mount
  useEffect(() => {
    if (patientId) {
      dispatch(fetchClinicalNotes(patientId));
    }
  }, [dispatch, patientId]);

  // Handlers
  const handleOpenDialog = useCallback((note = null, templateType = null) => {
    if (note) {
      setSelectedNote(note);
      const noteType = NOTE_TYPES.find(nt => nt.value === note.type) || NOTE_TYPES[1];
      setActiveTab(NOTE_TYPES.findIndex(nt => nt.value === note.type));
      setFormData({
        ...formData,
        ...note,
        date: note.created_at 
          ? format(parseISO(note.created_at), 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd'),
        orientation: note.orientation || { time: false, place: false, person: false, situation: false },
      });
    } else if (templateType) {
      const template = NOTE_TYPES.find(nt => nt.value === templateType);
      if (template) {
        setActiveTab(NOTE_TYPES.findIndex(nt => nt.value === templateType));
        setFormData({
          ...formData,
          type: templateType,
          ...template.template,
          date: format(new Date(), 'yyyy-MM-dd'),
          title: '',
          content: '',
          tags: [],
          urgent: false,
        });
      }
      setSelectedNote(null);
    } else {
      setSelectedNote(null);
      setFormData({
        ...formData,
        type: NOTE_TYPES[activeTab].value,
        ...NOTE_TYPES[activeTab].template,
        date: format(new Date(), 'yyyy-MM-dd'),
        title: '',
        content: '',
        clinician: '',
        tags: [],
        urgent: false,
      });
    }
    setOpenDialog(true);
  }, [formData, activeTab]);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setSelectedNote(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!patientId) {
      alert('Patient ID is required');
      return;
    }

    try {
      const noteType = NOTE_TYPES[activeTab]?.value || 'progress_note';
      const noteData = {
        ...formData,
        type: noteType,
        patient_id: patientId,
      };

      if (selectedNote) {
        await dispatch(
          updateClinicalNote({
            patientId,
            noteId: selectedNote.id,
            noteData,
          })
        ).unwrap();
      } else {
        await dispatch(addClinicalNote({ patientId, noteData })).unwrap();
      }

      await dispatch(fetchClinicalNotes(patientId));
      handleCloseDialog();
      setSnackbar({ open: true, message: selectedNote ? 'Note updated successfully' : 'Note created successfully', severity: 'success' });
    } catch (error) {
      console.error('Error saving clinical note:', error);
      alert(`Failed to save note: ${error?.response?.data?.detail || error?.message || 'Unknown error'}`);
    }
  }, [patientId, activeTab, formData, selectedNote, dispatch, handleCloseDialog]);

  const handleDelete = useCallback(async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }

    try {
      await dispatch(deleteClinicalNote({ patientId, noteId })).unwrap();
      await dispatch(fetchClinicalNotes(patientId));
      setSnackbar({ open: true, message: 'Note deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Error deleting clinical note:', error);
      alert(`Failed to delete note: ${error?.response?.data?.detail || error?.message || 'Unknown error'}`);
    }
  }, [patientId, dispatch]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const noteType = NOTE_TYPES[newValue];
    setFormData(prev => ({
      ...prev,
      type: noteType.value,
      ...noteType.template,
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleOrientationChange = (field) => {
    setFormData(prev => ({
      ...prev,
      orientation: {
        ...prev.orientation,
        [field]: !prev.orientation[field],
      },
    }));
  };

  const getNoteTypeConfig = (type) => {
    return NOTE_TYPES.find(nt => nt.value === type) || NOTE_TYPES[1];
  };

  // Render MSE Tab
  const renderMSETab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Date"
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Clinician"
          name="clinician"
          value={formData.clinician}
          onChange={handleChange}
          placeholder="Your name"
        />
      </Grid>
      {['appearance', 'behavior', 'speech', 'mood', 'affect', 'thoughtProcess', 'thoughtContent', 'cognition', 'insight', 'judgment'].map((field) => (
        <Grid item xs={12} md={6} key={field}>
          <TextField
            fullWidth
            label={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
            name={field}
            value={formData[field]}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>
      ))}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Orientation
        </Typography>
        <FormGroup row>
          {['time', 'place', 'person', 'situation'].map((field) => (
            <FormControlLabel
              key={field}
              control={
                <Checkbox
                  checked={formData.orientation[field]}
                  onChange={() => handleOrientationChange(field)}
                />
              }
              label={field.charAt(0).toUpperCase() + field.slice(1)}
            />
          ))}
        </FormGroup>
      </Grid>
      <Grid item xs={12}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Risk Assessment
        </Alert>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.suicidalIdeation}
                onChange={(e) => setFormData(prev => ({ ...prev, suicidalIdeation: e.target.checked }))}
              />
            }
            label="Suicidal Ideation"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.homicidalIdeation}
                onChange={(e) => setFormData(prev => ({ ...prev, homicidalIdeation: e.target.checked }))}
              />
            }
            label="Homicidal Ideation"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hallucinations}
                onChange={(e) => setFormData(prev => ({ ...prev, hallucinations: e.target.checked }))}
              />
            }
            label="Hallucinations"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.delusions}
                onChange={(e) => setFormData(prev => ({ ...prev, delusions: e.target.checked }))}
              />
            }
            label="Delusions"
          />
        </FormGroup>
      </Grid>
    </Grid>
  );

  // Render Progress Note (SOAP) Tab
  const renderProgressNoteTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Date"
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Clinician"
          name="clinician"
          value={formData.clinician}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Subjective (S)"
          name="subjective"
          value={formData.subjective}
          onChange={handleChange}
          multiline
          rows={4}
          placeholder="Patient's report of symptoms, concerns, and history..."
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Objective (O)"
          name="objective"
          value={formData.objective}
          onChange={handleChange}
          multiline
          rows={4}
          placeholder="Observable findings, assessments, measurements..."
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Assessment (A)"
          name="assessment"
          value={formData.assessment}
          onChange={handleChange}
          multiline
          rows={4}
          placeholder="Clinical impression, diagnosis, analysis..."
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Plan (P)"
          name="plan"
          value={formData.plan}
          onChange={handleChange}
          multiline
          rows={4}
          placeholder="Treatment plan, interventions, follow-up..."
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Treatment Progress"
          name="treatmentProgress"
          value={formData.treatmentProgress}
          onChange={handleChange}
          multiline
          rows={3}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Next Steps"
          name="nextSteps"
          value={formData.nextSteps}
          onChange={handleChange}
          multiline
          rows={2}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Follow-up Date"
          type="date"
          name="followUpDate"
          value={formData.followUpDate}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
    </Grid>
  );

  // Render Discharge Summary Tab
  const renderDischargeSummaryTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Admission Date"
          type="date"
          name="admissionDate"
          value={formData.admissionDate}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Discharge Date"
          type="date"
          name="dischargeDate"
          value={formData.dischargeDate}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Reason for Admission"
          name="reasonForAdmission"
          value={formData.reasonForAdmission}
          onChange={handleChange}
          multiline
          rows={3}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Treatment Provided"
          name="treatmentProvided"
          value={formData.treatmentProvided}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Discharge Diagnosis"
          name="dischargeDiagnosis"
          value={formData.dischargeDiagnosis}
          onChange={handleChange}
          multiline
          rows={2}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Follow-up Instructions"
          name="followUpInstructions"
          value={formData.followUpInstructions}
          onChange={handleChange}
          multiline
          rows={3}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Discharge Plan"
          name="dischargePlan"
          value={formData.dischargePlan}
          onChange={handleChange}
          multiline
          rows={3}
        />
      </Grid>
    </Grid>
  );

  if (loading && allClinicalNotes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Clinical Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive clinical documentation and note management
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          {role === 'doctor' && (
            <>
              <Button
                variant="outlined"
                startIcon={<TemplateIcon />}
                onClick={() => setFilterMenuAnchor(document.getElementById('filter-button'))}
              >
                Templates
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              >
                New Note
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* Search and Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Search notes by title, content, or diagnosis..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Note Type</InputLabel>
            <Select
              value={filterType}
              label="Note Type"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              {NOTE_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            id="filter-button"
          >
            Filters
          </Button>
        </Grid>
      </Grid>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setFilterTag(''); setFilterMenuAnchor(null); }}>
          All Tags
        </MenuItem>
        {TAG_OPTIONS.map(tag => (
          <MenuItem
            key={tag}
            onClick={() => { setFilterTag(tag); setFilterMenuAnchor(null); }}
          >
            {tag}
          </MenuItem>
        ))}
      </Menu>

      {/* Notes List */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filteredNotes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm || filterType || filterTag 
              ? 'No notes match your filters'
              : 'No clinical notes yet'}
          </Typography>
          {role === 'doctor' && !searchTerm && !filterType && !filterTag && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Create First Note
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredNotes.map((note, index) => {
            const noteConfig = getNoteTypeConfig(note.type);
            return (
              <Grid item xs={12} key={note.id || index}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <NoteCard urgent={note.urgent}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            icon={noteConfig.icon}
                            label={noteConfig.label}
                            color={noteConfig.color}
                            size="small"
                          />
                          {note.urgent && (
                            <Chip
                              icon={<WarningIcon />}
                              label="Urgent"
                              color="error"
                              size="small"
                            />
                          )}
                          {note.tags?.map(tag => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {note.title || 'Untitled Note'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {format(parseISO(note.created_at), 'PPp')}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {note.content || note.subjective || note.assessment || 'No content'}
                        </Typography>
                      </Box>
                      {role === 'doctor' && (
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(note)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(note.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      )}
                    </Box>
                  </NoteCard>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create/Edit Note Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              {selectedNote ? 'Edit Clinical Note' : 'Create New Clinical Note'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
            {NOTE_TYPES.map(type => (
              <Tab
                key={type.value}
                icon={type.icon}
                label={type.label}
                iconPosition="start"
              />
            ))}
          </Tabs>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Note Title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter a descriptive title"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.urgent}
                      onChange={(e) => setFormData(prev => ({ ...prev, urgent: e.target.checked }))}
                    />
                  }
                  label="Mark as Urgent"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="General Content/Summary"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Optional: General summary or additional notes"
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={TAG_OPTIONS}
                  value={formData.tags || []}
                  onChange={(e, newValue) => setFormData(prev => ({ ...prev, tags: newValue }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder="Add tags for easy filtering"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {activeTab === 0 && renderMSETab()}
          {activeTab === 1 && renderProgressNoteTab()}
          {activeTab === 2 && renderDischargeSummaryTab()}
          {activeTab === 3 && renderDischargeSummaryTab()}
          {activeTab === 4 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Medication Review Notes"
                  placeholder="Document medication changes, side effects, adherence, and recommendations..."
                />
              </Grid>
            </Grid>
          )}
          {activeTab === 5 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Consultation Notes"
                  placeholder="Document consultation discussion points and recommendations..."
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnhancedClinicalNotes;

